import React from 'react';
import { createUseStyles } from 'react-jss';
import { S, TONES } from './tokens';

type Props = {
  icon?: string;
  title: string;
  children?: React.ReactNode;
  /** One next step, usually a button. */
  action?: React.ReactNode;
  tone?: 'neutral' | 'error';
  testId?: string;
  compact?: boolean;
};

const useStyles = createUseStyles({
  root: {
    display: 'grid',
    justifyItems: 'center',
    gap: 8,
    padding: '48px 24px',
    textAlign: 'center',
    fontFamily: S.ui,
    color: S.fg
  },
  compact: { padding: '24px 16px' },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    marginBottom: 4,
    borderRadius: 12,
    background: S.chromeHover,
    color: S.muted,
    '& .material-icons': { fontSize: 24 }
  },
  error: { background: TONES.red.bg, color: S.red },
  title: { margin: 0, fontSize: 16, fontWeight: 600 },
  body: { margin: 0, maxWidth: 440, fontSize: 14, lineHeight: 1.5, color: S.muted },
  action: { marginTop: 8 }
});

/** What to show when a list has nothing in it (or failed to load), and what to do next. */
const EmptyState: React.FC<Props> = ({ icon = 'inbox', title, children, action, tone = 'neutral', testId, compact }) => {
  const c = useStyles();
  return (
    <div className={[c.root, compact ? c.compact : ''].join(' ')} data-testid={testId || 'empty-state'} role={tone === 'error' ? 'alert' : undefined}>
      <span className={[c.icon, tone === 'error' ? c.error : ''].join(' ')} aria-hidden>
        <i className="material-icons">{tone === 'error' ? 'error_outline' : icon}</i>
      </span>
      <h3 className={c.title}>{title}</h3>
      {children && <p className={c.body}>{children}</p>}
      {action && <div className={c.action}>{action}</div>}
    </div>
  );
};

export default EmptyState;
